<template>
  <div class="reporting-section reporting-overview">
    <div class="section-header">
      <i class="material-icons section-icon">dashboard</i>
      <h2>{{ $t('dash.reporting.overview') }}</h2>
      <span v-if="prevLoading" class="prev-loading-badge">
        <span class="prev-loading-dot"></span>{{ $t('dash.reporting.prev_loading') }}
      </span>
    </div>
    <div class="metric-row" v-if="page">
      <MetricCard
        :value="stats.page.page_fans"
        :previous-value="prevPeriod ? prevPeriod.page_fans : null"
        :trend-unavailable="!!prevPeriod && prevPeriod.page_fans == null"
        :trend-unavailable-text="$t('dash.reporting.tooltip.followers_history_unavailable')"
        :label="$t('dash.reporting.followers')"
        icon="group"
      />
      <MetricCard
        :value="stats.page.nb_posts || 0"
        :previous-value="prevPeriod ? prevPeriod.nb_posts : null"
        :label="$t('dash.reporting.publications')"
        icon="edit"
      />
      <MetricCard
        :value="stats.page.totalImpressions || 0"
        :previous-value="prevPeriod ? prevPeriod.totalImpressions : null"
        :label="$t('dash.reporting.impression')"
        icon="visibility"
      />
      <MetricCard
        v-if="stats.page.totalUniqueImpressions != null && network === 'linkedin'"
        :value="stats.page.totalUniqueImpressions || 0"
        :previous-value="prevPeriod ? prevPeriod.totalUniqueImpressions : null"
        :label="$t('dash.reporting.unique_impression')"
        icon="visibility_off"
      />
      <Tooltip :text="networkTooltip('dash.reporting.tooltip.interactions', 'dash.reporting.tooltip.engagement_total')" position="top">
        <MetricCard
          :value="effectiveEngagement"
          :previous-value="prevPeriod ? prevPeriod.totalEngagement : null"
          :label="$t('dash.reporting.interactions')"
          icon="fa-solid fa-hand-point-up"
        />
      </Tooltip>
      <Tooltip
        v-if="stats.page.totalClicks != null && (network === 'facebook' || network === 'linkedin')"
        :text="clicksTooltip" :active="network === 'facebook'" position="top"
      >
        <MetricCard
          :value="stats.page.totalClicks || 0"
          :previous-value="prevPeriod ? prevPeriod.totalClicks : null"
          :label="$t('dash.reporting.clic')"
          icon="touch_app"
        />
      </Tooltip>
      <Tooltip v-if="hasEngagementRate" :text="engagementRateTooltip" position="top">
        <MetricCard
          :value="engagementRate"
          :previous-value="prevEngagementRate"
          :label="$t('dash.reporting.engagement_rate')"
          icon="show_chart"
          format="percent"
        />
      </Tooltip>
    </div>
  </div>
</template>

<script>
import MetricCard from './panels/MetricCard.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
export default {
  name: 'ReportingOverview',
  components: { MetricCard, Tooltip },
  props: {
    stats: { type: Object, required: true },
    page: { type: Object, default: null },
    dateStart: { default: null },
    dateEnd: { default: null },
    prevLoading: { type: Boolean, default: false },
  },
  computed: {
    network() {
      return this.page ? (this.page.network || '') : ''
    },
    effectiveEngagement() {
      const p = this.stats.page
      if (p && p.totalEngagement) return p.totalEngagement
      return (this.stats.posts || []).reduce(function(s, post) {
        return s + (post.post_reactions_sum || post.reactions_count || post.like_count || 0)
               + (post.comments_count || 0)
               + (post.shares_count || 0)
      }, 0)
    },
    // Facebook : la carte Clics compte les clics sur liens ; le tooltip donne aussi le total tous clics
    clicksTooltip() {
      var total = (this.stats.posts || []).reduce(function(s, p) { return s + (p.total_clicks || p.link_clicks || 0) }, 0)
      return this.$t('dash.reporting.tooltip.clicks_card_facebook', { total: total.toLocaleString() })
    },
    // Tooltip propre au réseau (clé <base>_<network>), repli sur la clé générique
    networkTooltip() {
      var self = this
      return function(base, fallback) {
        var key = base + '_' + self.network
        return self.$te(key) ? self.$t(key) : self.$t(fallback || base)
      }
    },
    engagementRateTooltip() {
      // Formule réelle du taux, propre à chaque réseau (cf. reportingFormat / reporting-legacy.reportingGraphs)
      return this.networkTooltip('dash.reporting.tooltip.engagement_rate')
    },
    prevPeriod() {
      return this.stats.page && this.stats.page.previous_period || null
    },
    engagementRate() {
      const p = this.stats.page
      const reach = parseFloat(p.totalReachEngag)
      if (Number.isFinite(reach)) return reach
      const rate = parseFloat(p.engagement_rate)
      if (Number.isFinite(rate)) return rate
      const imp = p.totalImpressions || 0
      const eng = p.totalEngagement || 0
      return imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : 0
    },
    prevEngagementRate() {
      const prev = this.prevPeriod
      if (!prev) return null
      const reach = parseFloat(prev.totalReachEngag)
      if (Number.isFinite(reach)) return reach
      const rate = parseFloat(prev.engagement_rate)
      if (Number.isFinite(rate)) return rate
      const imp = prev.totalImpressions || 0
      const eng = prev.totalEngagement || 0
      return imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : null
    },
    hasEngagementRate() {
      const p = this.stats.page
      return Number.isFinite(parseFloat(p.totalReachEngag)) ||
             Number.isFinite(parseFloat(p.engagement_rate)) ||
             (p.totalImpressions || 0) > 0
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon {
  color: #6B1F4D;
  font-size: 22px;
}
.section-header h2 {
  font-size: 16px;
  font-weight: 700;
  color: #121826;
  margin: 0;
}
.prev-loading-badge {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #9ba3b7;
  background: #f9f7fb;
  border: 1px solid #f1edf5;
  padding: 2px 10px;
  border-radius: 20px;
}
.prev-loading-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #6B1F4D;
  animation: prev-pulse 1.2s ease-in-out infinite;
  flex-shrink: 0;
}
@keyframes prev-pulse {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50%       { opacity: 1;   transform: scale(1);   }
}
.metric-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}
/* carte enveloppée dans un Tooltip : même largeur qu'une MetricCard directe */
.metric-row > .hasTooltip {
  flex: 1;
  min-width: 160px;
  display: flex;
}
</style>
